import { localStorageService } from '../../../utils/local-storage-service';

/**
 * Centralized localStorage key prefixes for payroll features.
 * All payroll-related storage should go through payrollStorageHelpers.
 */
const STORAGE_KEYS = {
  ACKNOWLEDGED_RECORDS: 'payroll-acknowledged-records-',
  RECORDS_ELIGIBLE_FOR_EDIT: 'payroll-record-id-eligible-for-edit-',
} as const;

/**
 * Helper functions for payroll-related localStorage operations.
 * All payroll storage should go through these helpers.
 */
export const payrollStorageHelpers = {
  /**
   * Get acknowledged record IDs as a Set
   */
  getAcknowledgedRecords(payrollId: string): Set<string> {
    const key = STORAGE_KEYS.ACKNOWLEDGED_RECORDS + payrollId;
    const records = localStorageService.get<string[]>(key);

    return records ? new Set(records) : new Set();
  },

  /**
   * Save acknowledged record IDs from a Set
   */
  setAcknowledgedRecords(payrollId: string, recordIds: Set<string>): void {
    const key = STORAGE_KEYS.ACKNOWLEDGED_RECORDS + payrollId;

    localStorageService.set(key, JSON.stringify(Array.from(recordIds)));
  },

  /**
   * Add a record to the eligible-for-edit list
   */
  addRecordEligibleForEdit(payrollId: string, recordId: string): void {
    const key = STORAGE_KEYS.RECORDS_ELIGIBLE_FOR_EDIT + payrollId;
    const existing = localStorageService.get<string[]>(key) ?? [];

    if (!existing.includes(recordId)) {
      localStorageService.set(key, JSON.stringify([...existing, recordId]));
    }
  },

  /**
   * Check if a record is eligible for edit
   */
  isRecordEligibleForEdit(payrollId: string, recordId: string): boolean {
    const key = STORAGE_KEYS.RECORDS_ELIGIBLE_FOR_EDIT + payrollId;
    const records = localStorageService.get<string[]>(key) ?? [];

    return records.includes(recordId);
  },

  /**
   * Remove acknowledged records for a payroll
   */
  removeAcknowledgedRecords(payrollId: string): void {
    const key = STORAGE_KEYS.ACKNOWLEDGED_RECORDS + payrollId;

    localStorageService.remove(key);
  },

  /**
   * Clear ALL payroll-related storage for a specific payroll
   */
  clearAllPayrollStorage(payrollId: string): void {
    Object.values(STORAGE_KEYS).forEach((prefix) => {
      localStorageService.remove(prefix + payrollId);
    });
  },
};
